import { lazy, Suspense, useEffect, useState } from 'react';
import { MenuToggle } from './components/MenuToggle';
import { Routes, Route, Navigate, NavLink, useNavigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { getCurrentSession, logout, SessionInfo } from './lib/auth';
import { api, ClientProfile } from './lib/api';
import { getActiveProfileId, setActiveProfileId } from './lib/activeProfile';
import { ProfilePicker } from './components/ProfilePicker';
import { HomePage } from './pages/HomePage';
import { Skeleton } from './components/Skeleton';
import { translateError } from './lib/errors';
import { safeRedirectTarget } from './lib/redirect';
import { useRobotsMeta } from './lib/useRobotsMeta';

const LoginPage = lazy(() => import('./pages/LoginPage').then((pageModule) => ({ default: pageModule.LoginPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((pageModule) => ({ default: pageModule.ForgotPasswordPage })),
);
const ClientsPage = lazy(() =>
  import('./pages/ClientsPage').then((pageModule) => ({ default: pageModule.ClientsPage })),
);
const ClientFormPage = lazy(() =>
  import('./pages/ClientFormPage').then((pageModule) => ({ default: pageModule.ClientFormPage })),
);
const ClientDetailPage = lazy(() =>
  import('./pages/ClientDetailPage').then((pageModule) => ({ default: pageModule.ClientDetailPage })),
);
const CustomerUsersPage = lazy(() =>
  import('./pages/CustomerUsersPage').then((pageModule) => ({ default: pageModule.CustomerUsersPage })),
);
const RunsPage = lazy(() => import('./pages/RunsPage').then((pageModule) => ({ default: pageModule.RunsPage })));
const UsagePage = lazy(() => import('./pages/UsagePage').then((pageModule) => ({ default: pageModule.UsagePage })));
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((pageModule) => ({ default: pageModule.DashboardPage })),
);
const SignalDetailPage = lazy(() =>
  import('./pages/SignalDetailPage').then((pageModule) => ({ default: pageModule.SignalDetailPage })),
);
const CollectionsPage = lazy(() =>
  import('./pages/CollectionsPage').then((pageModule) => ({ default: pageModule.CollectionsPage })),
);
const CollectionDetailPage = lazy(() =>
  import('./pages/CollectionDetailPage').then((pageModule) => ({ default: pageModule.CollectionDetailPage })),
);
const SourceUnavailablePage = lazy(() =>
  import('./pages/SourceUnavailablePage').then((pageModule) => ({ default: pageModule.SourceUnavailablePage })),
);
const SourceTextPage = lazy(() =>
  import('./pages/SourceTextPage').then((pageModule) => ({ default: pageModule.SourceTextPage })),
);

export function App() {
  useRobotsMeta();
  const [session, setSession] = useState<SessionInfo | null | undefined>(undefined);
  const [profiles, setProfiles] = useState<ClientProfile[] | null>(null);
  const [bootError, setBootError] = useState('');
  const [bootRetry, setBootRetry] = useState(0);

  useEffect(() => {
    getCurrentSession().then((session) => setSession(session));
  }, []);

  useEffect(() => {
    if (!session) {
      setProfiles(null);
      return;
    }
    setBootError('');
    api
      .myProfiles()
      .then((list) => {
        setProfiles(list);
        const current = getActiveProfileId();
        const validIds = new Set(list.map((profile) => profile.profileId));
        if (!current || !validIds.has(current)) {
          if (list.length > 0) {
            setActiveProfileId(list[0].profileId);
          } else {
            setActiveProfileId(null);
          }
        }
      })
      .catch((e) => setBootError(translateError(e)));
  }, [session, bootRetry]);

  if (session === undefined) {
    return <Skeleton rows={3} label="Sessie laden" />;
  }

  if (session === null) {
    return (
      <Suspense fallback={<Skeleton rows={3} label="Pagina laden" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/bron-onbereikbaar" element={<SourceUnavailablePage />} />
          <Route path="/login" element={<LoginPage onLogin={(session) => setSession(session)} />} />
          <Route path="/login/forgot" element={<ForgotPasswordPage />} />
          <Route path="*" element={<RedirectToLogin />} />
        </Routes>
      </Suspense>
    );
  }

  const defaultRoute = session.role === 'backoffice' ? '/admin/profiles' : '/app';

  if (profiles === null && !bootError) {
    return <Skeleton rows={3} label="Profielen laden" />;
  }

  if (session.role === 'customer' && profiles && profiles.length === 0) {
    return (
      <div className="empty">
        <p>Je hebt nog geen toegang tot een profiel. Vraag de beheerder van jouw organisatie om je toe te voegen.</p>
        <button
          className="btn secondary"
          onClick={() => {
            logout();
            setSession(null);
          }}
        >
          Uitloggen
        </button>
      </div>
    );
  }

  return (
    <Suspense fallback={<Skeleton rows={3} label="Pagina laden" />}>
      <Routes>
        <Route path="/" element={<HomePage loggedInTo={defaultRoute} />} />
        <Route path="/bron-onbereikbaar" element={<SourceUnavailablePage />} />
        <Route
          element={
            <Shell
              session={session}
              profiles={profiles ?? []}
              onLogout={() => setSession(null)}
              bootError={bootError}
              onBootRetry={() => setBootRetry((count) => count + 1)}
            />
          }
        >
          {session.role === 'backoffice' ? (
            <>
              <Route path="/admin/profiles" element={<ClientsPage />} />
              <Route path="/admin/profiles/new" element={<ClientFormPage mode="create" />} />
              <Route path="/admin/profiles/:id" element={<ClientDetailPage />} />
              <Route path="/admin/profiles/:id/edit" element={<ClientFormPage mode="edit" />} />
              <Route path="/admin/profiles/:id/signals/:docId" element={<SignalDetailPage backoffice />} />
              <Route path="/admin/profiles/:id/signals/:docId/brontekst" element={<SourceTextPage backoffice />} />
              <Route path="/admin/users" element={<CustomerUsersPage />} />
              <Route path="/admin/usage" element={<UsagePage />} />
              <Route path="/admin/runs" element={<RunsPage />} />
            </>
          ) : (
            <>
              <Route path="/app" element={<DashboardPage />} />
              {/* Gedeelde links wijzen naar /app/signals?priority=high. Die
                  route bestond niet en viel in de catch-all, waarna de
                  querystring stil verdween. Zelfde pagina, geen redirect, dus
                  de zoekparameters blijven staan. */}
              <Route path="/app/signals" element={<DashboardPage />} />
              <Route path="/app/signals/:docId" element={<SignalDetailPage />} />
              <Route path="/app/signals/:docId/brontekst" element={<SourceTextPage />} />
              <Route path="/app/collections" element={<CollectionsPage />} />
              <Route path="/app/collections/:collectionId" element={<CollectionDetailPage />} />
              <Route path="/app/collections/:collectionId/signals/:docId" element={<SignalDetailPage />} />
            </>
          )}
          <Route path="*" element={<RedirectAfterLogin fallback={defaultRoute} />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

// Deeplinks uit de signaalmail komen binnen bij een uitgelogde gebruiker. Zonder
// deze twee componenten wordt de bestemming weggegooid door de redirect naar
// /login en landt de klant na inloggen op het dashboard in plaats van op het stuk.
function RedirectToLogin() {
  const location = useLocation();
  const target = location.pathname + location.search;
  const suffix = target === '/' ? '' : `?redirectTo=${encodeURIComponent(target)}`;
  return <Navigate to={`/login${suffix}`} replace />;
}

function RedirectAfterLogin({ fallback }: { fallback: string }) {
  const [params] = useSearchParams();
  return <Navigate to={safeRedirectTarget(params.get('redirectTo')) ?? fallback} replace />;
}

// Alleen interne paden. Een absolute of protocol-relatieve URL zou dit een
// open redirect maken, en /login terug zou een lus opleveren.
export type ShellContext = { session: SessionInfo; profiles: ClientProfile[] };

function Shell({
  session,
  profiles,
  onLogout,
  bootError,
  onBootRetry,
}: {
  session: SessionInfo;
  profiles: ClientProfile[];
  onLogout: () => void;
  bootError: string;
  onBootRetry: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="app">
      <a href="#hoofdinhoud" className="skip-link">
        Naar de inhoud
      </a>
      <div className="mobile-topbar">
        <img src="/logo-horizontal-mono-light.svg" alt="Beleidssignaal" />
        <MenuToggle open={navOpen} onToggle={() => setNavOpen((isOpen) => !isOpen)} controls="app-sidebar" />
      </div>
      <div className={`sidebar-backdrop${navOpen ? ' open' : ''}`} onClick={() => setNavOpen(false)} />
      <nav id="app-sidebar" className={`sidebar${navOpen ? ' open' : ''}`}>
        <img src="/logo-horizontal-mono-light.svg" alt="Beleidssignaal" className="sidebar-logo" />
        {session.role === 'backoffice' ? (
          <>
            <NavLink to="/admin/profiles" className={({ isActive }) => (isActive ? 'active' : '')}>
              Profielen
            </NavLink>
            <NavLink to="/admin/users" className={({ isActive }) => (isActive ? 'active' : '')}>
              Gebruikers
            </NavLink>
            <NavLink to="/admin/usage" className={({ isActive }) => (isActive ? 'active' : '')}>
              Verbruik
            </NavLink>
            <NavLink to="/admin/runs" className={({ isActive }) => (isActive ? 'active' : '')}>
              Analyses
            </NavLink>
          </>
        ) : (
          <>
            <NavLink to="/app" end className={({ isActive }) => (isActive ? 'active' : '')}>
              Signalen
            </NavLink>
            <NavLink to="/app/collections" className={({ isActive }) => (isActive ? 'active' : '')}>
              Dossiers
            </NavLink>
          </>
        )}
        <div className="spacer" />
        {session.role === 'customer' && profiles.length > 1 && (
          <div className="sidebar-profile-picker">
            <ProfilePicker profiles={profiles} />
          </div>
        )}
        <div className="me">
          <div>{session.email}</div>
          <div className="me-role">{session.role}</div>
        </div>
        <button
          className="logout"
          onClick={() => {
            logout();
            onLogout();
            navigate('/login');
          }}
        >
          Uitloggen
        </button>
      </nav>
      <main className="main" id="hoofdinhoud">
        {bootError && (
          <div className="error row between gap-3">
            <span>Er ging iets mis bij het laden: {bootError}</span>
            <button className="btn secondary" onClick={onBootRetry}>
              Probeer opnieuw
            </button>
          </div>
        )}
        <Outlet context={{ session, profiles }} />
      </main>
    </div>
  );
}
